import { useMemo, useState } from 'react';
import { Pagination } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { AuditLedgerTable, AuditLedgerFilters } from '../components/Activity/index.js';
import { useAuditLedger } from '../hooks/useAuditLedger.js';
import { useInboxPagination } from '../hooks/useInboxPagination.js';

/**
 * Activity Log — Audit Ledger aligned with admin-studio provider Audit:
 * Filters (fixed) → scrollable table → pagination (fixed). CT-4866.
 */
function ActivityLogPage() {
  const { t } = useTranslation('work-queues-app');
  const [range, setRange] = useState('all');
  const [search, setSearch] = useState('');
  const { entries } = useAuditLedger({ range, search });

  const paginationResetKey = useMemo(() => `${range}|${search}`, [range, search]);
  const {
    pageItems,
    rangeLabel,
    totalItems,
    safePage,
    totalPages,
    perPage,
    hasPrevious,
    hasNext,
    onPrevPage,
    onNextPage,
    onPageSizeChange,
  } = useInboxPagination(entries, paginationResetKey);

  const paginationLabels = useMemo(
    () => ({
      rowsPerPage: t('pagination.rowsPerPage'),
      items: t('pagination.items', { range: rangeLabel, count: totalItems }),
      previousPage: t('pagination.previous'),
      nextPage: t('pagination.next'),
    }),
    [t, rangeLabel, totalItems],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
      <div className="shrink-0">
        <AuditLedgerFilters
          range={range}
          onRangeChange={setRange}
          search={search}
          onSearchChange={setSearch}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-2 scrollbar-aimsos-auto-sm">
        <AuditLedgerTable entries={pageItems} />
      </div>

      <div className="shrink-0 py-3">
        <Pagination
          rangeLabel={rangeLabel}
          totalItems={totalItems}
          currentPage={safePage}
          totalPages={totalPages}
          perPage={perPage}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
          onPrevPage={onPrevPage}
          onNextPage={onNextPage}
          onPageSizeChange={onPageSizeChange}
          labels={paginationLabels}
        />
      </div>
    </div>
  );
}

export default ActivityLogPage;
